import { LocaleProvider } from './i18n/LocaleProvider';
import { LanguageSuggestion } from './i18n/LanguageSelector';
import React, { Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ThemeProvider } from './components/ThemeProvider';
import CookieConsent from './components/CookieConsent';
import CalendlyWidget from './components/CalendlyWidget';
import BackToTop from './components/BackToTop';
import { usePageTracking, useCtaTracking } from './hooks/useAnalytics';

const queryClient = new QueryClient();

// Scroll to top on route change (client only). Hash-aware: on deep links
// (/page#section) re-assert the anchor position instead — the mount-time run
// otherwise fires after hydration and undoes the browser's native anchor scroll.
// The reset must be behavior:'instant': the global `html { scroll-behavior:
// smooth }` (for in-page anchors) otherwise animates it from the old page's
// offset, so short target pages appear to open at the bottom and crawl up.
const ScrollToTop = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      document.getElementById(location.hash.slice(1))?.scrollIntoView();
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname, location.hash]);
  return null;
};

const AnalyticsWrapper = ({ children }: { children: React.ReactNode }) => {
  usePageTracking();
  useCtaTracking();
  return <>{children}</>;
};

/**
 * Root layout route — holds all app-wide providers + persistent chrome and renders
 * the matched page via <Outlet />. This is the single root route so providers never
 * remount on navigation. vite-react-ssg creates the router; there is no BrowserRouter.
 */
const Layout = () => (
  <LocaleProvider>
  <ThemeProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <ScrollToTop />
        <AnalyticsWrapper>
          {/* Suspense boundary for lazily code-split route components. During SSG
              prerender the lazy route is resolved, so the fallback never reaches the
              static HTML; it only shows briefly on client-side navigation. min-h-screen
              keeps the footer from jumping (avoids CLS). */}
          <Suspense fallback={<div className="min-h-screen" />}>
            <Outlet />
          </Suspense>
        </AnalyticsWrapper>
        <LanguageSuggestion />
        <CookieConsent />
        <CalendlyWidget />
        <BackToTop />
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
  </LocaleProvider>
);

export default Layout;
